import React, { useState } from 'react';
import axios from 'axios';
import type { ReviewCycle } from './types';

interface CancelCycleButtonProps {
  cycle: ReviewCycle;
  // Caller passes in whether the current user is allowed to cancel — keeps
  // this component decoupled from the usePermissions hook shape. Caller
  // typically computes this as: isAdmin || canMakeReviewDecisions ||
  // canManageReviewSubmissions (mirrors backend route gate).
  canCancel: boolean;
  getAuthHeaders: () => Promise<Record<string, string>>;
  onComplete: () => void;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

const TERMINAL_STATUSES = new Set(['approved', 'rejected', 'canceled', 'superseded']);

/**
 * Phase 8E — admin / decision-maker action to cancel a mid-flight cycle.
 *
 * Renders only when:
 *  - the user holds content_review.manage_submissions or
 *    content_review.decision_maker (admin role inherits both)
 *  - the cycle is NOT in a terminal status (approved, rejected, canceled)
 *
 * Prompts for a cancellation reason — required by the backend audit and
 * helpful for the producer notification body.
 *
 * On success, the parent's onComplete() refreshes the cycle so the new
 * status='canceled' surfaces. The cycle stays in the DB for audit; lineage
 * collisions are released because submissionService.IN_FLIGHT_STATUSES does
 * not include 'canceled'.
 */
const CancelCycleButton: React.FC<CancelCycleButtonProps> = ({ cycle, canCancel, getAuthHeaders, onComplete }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!canCancel) return null;
  if (TERMINAL_STATUSES.has(cycle.status)) return null;

  const handleClick = async () => {
    const reason = window.prompt(
      'Cancel this review cycle?\n\nThis is an audited action — please enter a brief reason (producer left, content scrapped, etc.). Cancellation unblocks the lineage so a fresh submission with the same Type / Audience / Title can proceed.',
      ''
    );
    // Empty string = user clicked OK with no input (they want to cancel
    // anyway); null = they hit Cancel.
    if (reason === null) return;
    const trimmed = reason.trim();
    if (!trimmed) {
      const proceed = window.confirm('No reason entered. Cancel anyway? (Audit row will be empty.)');
      if (!proceed) return;
    }

    try {
      setLoading(true);
      setError(null);
      const headers = await getAuthHeaders();
      await axios.post(
        `${backendUrl}/api/content-review/cycles/${cycle._id}/cancel`,
        { reason: trimmed },
        { headers }
      );
      onComplete();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to cancel cycle');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ display: 'inline-block', marginLeft: 8 }}>
      <button
        className="review-btn"
        style={{
          background: '#fff',
          border: '1px solid #dc3545',
          color: '#dc3545',
          padding: '6px 12px',
          borderRadius: 4,
          cursor: loading ? 'wait' : 'pointer',
          fontSize: 14
        }}
        onClick={handleClick}
        disabled={loading}
        title="Cancel this cycle (admin / decision maker only)"
      >
        {loading ? 'Canceling...' : 'Cancel cycle'}
      </button>
      {error && <span style={{ color: '#dc3545', fontSize: 12, marginLeft: 8 }}>{error}</span>}
    </div>
  );
};

export default CancelCycleButton;
